/**
 * Pro 2 UltraClarity: the "VITURE Pro 2" wordmark section from viture.com/pro2 (its "s06").
 * The wordmark is drawn to a canvas and used as a mask over the video. While the section is pinned,
 * scroll slides the wordmark left, then zooms through one of its letters until the video fills the
 * screen, and a glass card rises with the UltraClarity copy before swapping to the second heading.
 * Layout values, timeline positions and the letter-finding maths are taken from the source page.
 */
if (!customElements.get('pro2-ultra')) {
  const MOBILE_QUERY = '(max-width: 640px)';

  /**
   * @typedef {object} StageConfig
   * @property {string} sel Selector of the stage element
   * @property {number} w Design width
   * @property {number} h Design height
   * @property {number} font Wordmark font size
   * @property {number} rightEdge Where the wordmark's right edge sits before the slide
   * @property {number} startShift Slide start, as a share of the wordmark width
   * @property {number} endShift Slide end, as a share of the wordmark width
   * @property {number} zoomMax How far the zoom through the letter scales the mask
   * @property {number} cardTop Card top position on the stage
   * @property {boolean} mobile
   */

  /**
   * @typedef {object} StageParts
   * @property {HTMLElement} stage
   * @property {HTMLElement} bg
   * @property {HTMLElement} dimOut
   * @property {HTMLElement} photo
   * @property {HTMLElement} vid
   * @property {HTMLElement} wrap Mask wrapper, moved and scaled with the wordmark
   * @property {HTMLElement} inner Mask content, counter-transformed so the video stays put
   * @property {HTMLElement} tex
   * @property {HTMLElement} shade
   * @property {HTMLElement} card
   * @property {HTMLElement} ca First card copy
   * @property {HTMLElement} cb Second card copy
   */

  /**
   * @typedef {object} Geometry
   * @property {StageConfig} cfg
   * @property {StageParts} el
   * @property {number} slide Distance the wordmark slides
   * @property {number} zoom Zoom factor applied on top of the end of the slide
   * @property {number} cardH Card height with the first copy
   * @property {number} cardEndH Card height with the second copy
   * @property {number} cardFrom Card offset before it rises
   * @property {number} caOut First copy's offset once swapped out
   * @property {number} cbIn Second copy's offset before it swaps in
   */

  // Per-stage wordmark and card geometry from the source
  /** @type {{ desktop: StageConfig, mobile: StageConfig }} */
  const STAGES = {
    desktop: {
      sel: '.pro2-ultra__stage--d',
      w: 1920,
      h: 1080,
      font: 400,
      rightEdge: 1573.5,
      startShift: 0.5,
      endShift: -0.05,
      zoomMax: 16,
      cardTop: 224,
      mobile: false,
    },
    mobile: {
      sel: '.pro2-ultra__stage--m',
      w: 375,
      h: 812,
      font: 240,
      rightEdge: 748.5,
      startShift: 0.5,
      endShift: -0.3,
      zoomMax: 12,
      cardTop: 299.5,
      mobile: true,
    },
  };

  // How far (ms time constant) the scrubbed timeline trails the scroll position
  const SMOOTHING_MS = 200;

  /**
   * @param {number} value
   * @returns {number}
   */
  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  /**
   * Progress of a tween starting at `start` lasting `duration`, on the section's 0–1 timeline
   * @param {number} time
   * @param {number} start
   * @param {number} duration
   * @returns {number}
   */
  const tweenProgress = (time, start, duration) => clamp01((time - start) / duration);

  /** @param {number} progress */
  const power1InOut = (progress) => (progress < 0.5 ? 2 * progress * progress : 1 - (-2 * progress + 2) ** 2 / 2);

  /**
   * The slide holds for its first quarter
   * @param {number} progress
   */
  const afterHold = (progress) => (progress <= 0.25 ? 0 : (progress - 0.25) / 0.75);

  /**
   * Draw the wordmark in white on a canvas at `font` px, oversampled for sharp edges (returned as R)
   * @param {string} text
   * @param {number} font
   */
  function drawWordmark(text, font) {
    const pad = Math.round(0.25 * font);
    let context = /** @type {CanvasRenderingContext2D} */ (document.createElement('canvas').getContext('2d'));
    context.font = `400 ${font}px "Season Sans", sans-serif`;
    const textWidth = context.measureText(text).width;
    const oversample = Math.max(
      2,
      Math.min(Math.min(4, Math.max(3, 2 * Math.ceil(window.devicePixelRatio))), 8192 / (textWidth + 2 * pad))
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil((textWidth + 2 * pad) * oversample);
    canvas.height = Math.ceil((font + 2 * pad) * oversample);
    context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d', { willReadFrequently: true }));
    context.font = `400 ${font * oversample}px "Season Sans", sans-serif`;
    context.fillStyle = '#fff';
    const metrics = context.measureText(text);
    context.fillText(
      text,
      pad * oversample,
      canvas.height / 2 + (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2
    );
    return { canvas, ctx: context, w: textWidth, pad, R: oversample };
  }

  class Pro2Ultra extends HTMLElement {
    connectedCallback() {
      // TS sees properties first set outside a constructor as possibly undefined, so later reads of
      // ones that connectedCallback always sets are cast back to their declared type
      /** @type {HTMLElement} */
      this.track = /** @type {HTMLElement} */ (this.querySelector('.pro2-ultra__track'));
      /** @type {HTMLElement} */
      this.sticky = /** @type {HTMLElement} */ (this.querySelector('.pro2-ultra__sticky'));
      /** @type {MediaQueryList} */
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /** @type {number} Where the timeline is drawn now, easing toward `target` */
      this.time = 0;
      /** @type {number} Where the scroll position says the timeline should be */
      this.target = 0;
      /** @type {number} */
      this.buildId = 0;

      // Keep the outer video and the one inside the wordmark on the same frame
      this.querySelectorAll('.pro2-ultra__stage').forEach((stage) => {
        const outer = /** @type {HTMLVideoElement | null} */ (stage.querySelector('.pro2-ultra__vid video'));
        const inner = /** @type {HTMLVideoElement | null} */ (stage.querySelector('.pro2-ultra__mvid video'));
        if (!outer || !inner) return;
        const sync = () => {
          if (inner.readyState >= 1 && Math.abs(inner.currentTime - outer.currentTime) > 0.1) {
            inner.currentTime = outer.currentTime;
          }
        };
        outer.addEventListener('playing', sync);
        inner.addEventListener('playing', sync);
      });

      /** @type {() => void} */
      this.onScroll = () => {
        this.target = this.#scrollTime();
        this.#videos();
        if (!this.raf) this.raf = requestAnimationFrame(/** @type {FrameRequestCallback} */ (this.tick));
      };
      /** @type {() => void} */
      this.onResize = () => {
        // Undefined before the first resize, which cancelAnimationFrame ignores
        cancelAnimationFrame(/** @type {number} */ (this.resizeFrame));
        /** @type {number} */
        this.resizeFrame = requestAnimationFrame(() => this.#layout());
      };
      /** @type {FrameRequestCallback} Ease the drawn time toward the scroll position, then stop once caught up */
      this.tick = (now) => {
        const elapsed = Math.min(now - (this.lastTick ?? now), 64);
        this.lastTick = now;
        /** @type {number} */ (this.time) +=
          (/** @type {number} */ (this.target) - /** @type {number} */ (this.time)) *
          (1 - Math.exp(-elapsed / SMOOTHING_MS));
        if (Math.abs(/** @type {number} */ (this.target) - /** @type {number} */ (this.time)) < 0.0005)
          this.time = this.target;
        this.#render();
        if (this.time === this.target) {
          this.raf = null;
          this.lastTick = null;
        } else {
          this.raf = requestAnimationFrame(/** @type {FrameRequestCallback} */ (this.tick));
        }
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.mobileQuery.addEventListener('change', this.onResize);

      if (!this.reducedMotion) {
        this.classList.add('is-armed');
        /** @type {IntersectionObserver} */
        this.revealObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.classList.add('is-revealed');
            /** @type {IntersectionObserver} */ (this.revealObserver).disconnect();
          },
          { rootMargin: '0px 0px -22% 0px' }
        );
        this.revealObserver.observe(this);
      }

      this.#layout();
      this.#videos();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', /** @type {() => void} */ (this.onScroll));
      window.removeEventListener('resize', /** @type {() => void} */ (this.onResize));
      /** @type {MediaQueryList} */ (this.mobileQuery).removeEventListener(
        'change',
        /** @type {() => void} */ (this.onResize)
      );
      this.revealObserver?.disconnect();
      cancelAnimationFrame(/** @type {number} */ (this.resizeFrame));
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    /** @returns {number} 0–1 across the pinned scroll */
    #scrollTime() {
      const rect = /** @type {HTMLElement} */ (this.track).getBoundingClientRect();
      const distance = rect.height - /** @type {HTMLElement} */ (this.sticky).clientHeight;
      return distance > 0 ? clamp01(-rect.top / distance) : 0;
    }

    /** Scale the active stage to cover the viewport, then build its wordmark mask and card geometry */
    async #layout() {
      const mobile = /** @type {MediaQueryList} */ (this.mobileQuery).matches;
      const config = mobile ? STAGES.mobile : STAGES.desktop;
      const width = /** @type {HTMLElement} */ (this.sticky).clientWidth;
      const height = /** @type {HTMLElement} */ (this.sticky).clientHeight;
      if (mobile) {
        // Mobile stage is 375 wide and as tall as the viewport at that width
        /** @type {number} */
        this.scale = width / config.w;
        this.style.setProperty('--stage-h', (height / this.scale).toFixed(2));
      } else {
        this.scale = Math.max(width / config.w, height / config.h);
        this.style.setProperty('--stage-h', String(config.h));
      }
      this.style.setProperty('--scale', this.scale.toFixed(4));

      const thisBuild = ++(/** @type {number} */ (this.buildId));
      try {
        await document.fonts.load(`400 ${config.font}px "Season Sans"`);
      } catch {
        // Falls back to the default sans-serif
      }
      if (thisBuild !== this.buildId || !this.isConnected) return;
      /** @type {Geometry | undefined} */
      this.geo = this.#build(config);
      this.time = this.target = this.#scrollTime();
      this.#render();
    }

    /**
     * @param {StageConfig} config
     * @returns {Geometry}
     */
    #build(config) {
      const stage = /** @type {HTMLElement} */ (this.querySelector(config.sel));
      /** @type {StageParts} */
      const parts = {
        stage,
        bg: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__bg img')),
        dimOut: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__dim--out')),
        photo: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__photo')),
        vid: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__vid')),
        wrap: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__maskwrap')),
        inner: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__maskinner')),
        tex: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__tex')),
        shade: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__shade')),
        card: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__card')),
        ca: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__ca')),
        cb: /** @type {HTMLElement} */ (stage.querySelector('.pro2-ultra__cb')),
      };

      // Wordmark: right edge at rightEdge, slides from startShift to endShift of its width
      const {
        canvas,
        ctx: context,
        w: wordWidth,
        pad,
        R: oversample,
      } = drawWordmark(stage.dataset.wordmark || 'VITURE Pro 2', config.font);
      const top = config.h / 2 - config.font / 2;
      const baseX = config.rightEdge - wordWidth;
      const startX = baseX + config.startShift * wordWidth;
      const endX = baseX + config.endShift * wordWidth;
      const slide = endX - startX;

      const wrapStyle = parts.wrap.style;
      const maskUrl = `url(${canvas.toDataURL()})`;
      wrapStyle.maskImage = wrapStyle.webkitMaskImage = maskUrl;
      wrapStyle.maskRepeat = wrapStyle.webkitMaskRepeat = 'no-repeat';
      wrapStyle.maskSize = wrapStyle.webkitMaskSize = `${wordWidth + 2 * pad}px ${config.font + 2 * pad}px`;
      wrapStyle.maskPosition = wrapStyle.webkitMaskPosition = `${startX - pad}px ${top - pad}px`;
      wrapStyle.width = `${Math.ceil(startX + wordWidth + pad)}px`;
      wrapStyle.visibility = 'visible';

      // Zoom origin: the solid point of a letter nearest the screen centre once the slide ends
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      /**
       * Alpha of the canvas pixel nearest a point, 0 outside the canvas
       * @param {number} canvasX
       * @param {number} canvasY
       * @returns {number}
       */
      const alpha = (canvasX, canvasY) => {
        const pixelX = Math.round(canvasX);
        const pixelY = Math.round(canvasY);
        return pixelX < 0 || pixelY < 0 || pixelX >= canvas.width || pixelY >= canvas.height
          ? 0
          : /** @type {number} */ (pixels[(pixelY * canvas.width + pixelX) * 4 + 3]);
      };
      const probeRadius = 0.03 * config.font * oversample;
      /**
       * Whether a point and its four neighbours `probeRadius` away are all solid
       * @param {number} canvasX
       * @param {number} canvasY
       */
      const solid = (canvasX, canvasY) =>
        alpha(canvasX, canvasY) > 250 &&
        alpha(canvasX - probeRadius, canvasY) > 250 &&
        alpha(canvasX + probeRadius, canvasY) > 250 &&
        alpha(canvasX, canvasY - probeRadius) > 250 &&
        alpha(canvasX, canvasY + probeRadius) > 250;
      const centreX = (config.w / 2 - endX + pad) * oversample;
      const centreY = (config.h / 2 - top + pad) * oversample;
      let focusX = centreX;
      let focusY = centreY;
      if (!solid(focusX, focusY)) {
        search: for (let radius = 6; radius < config.font * oversample; radius += 6) {
          const steps = Math.max(8, Math.round((2 * Math.PI * radius) / 8));
          for (let step = 0; step < steps; step++) {
            const angle = (step / steps) * 2 * Math.PI;
            const pointX = centreX + radius * Math.cos(angle);
            const pointY = centreY + radius * Math.sin(angle);
            if (solid(pointX, pointY)) {
              focusX = pointX;
              focusY = pointY;
              break search;
            }
          }
        }
      }
      const origin = `${focusX / oversample - pad + endX - slide}px ${focusY / oversample - pad + top}px`;
      parts.wrap.style.transformOrigin = origin;
      parts.inner.style.transformOrigin = origin;

      // Card: starts below the stage, rises, then swaps content A for B and shrinks to fit
      parts.card.style.height = '';
      parts.card.style.top = '';
      const cardStartHeight = parts.card.offsetHeight;
      const firstCopyHeight = parts.ca.offsetHeight;
      const cardEndHeight = cardStartHeight - firstCopyHeight + parts.cb.offsetHeight;
      const cardStartOffset = config.mobile
        ? config.h - config.cardTop + cardStartHeight / 2 + 40
        : config.h - config.cardTop + 40;
      const firstCopyExit = -(firstCopyHeight + (config.mobile ? 8 : 12) + 6);
      const secondCopyEntry = firstCopyHeight + (config.mobile ? 24 : 40);

      return {
        cfg: config,
        el: parts,
        slide,
        zoom: config.zoomMax / 1.06,
        cardH: cardStartHeight,
        cardEndH: cardEndHeight,
        cardFrom: cardStartOffset,
        caOut: firstCopyExit,
        cbIn: secondCopyEntry,
      };
    }

    /**
     * Move/scale the mask while counter-transforming its content so the video stays put
     * @param {number} offsetX
     * @param {number} scale
     */
    #mask(offsetX, scale) {
      // Only called from #render, after the geometry has been built
      const { wrap, inner } = /** @type {Geometry} */ (this.geo).el;
      wrap.style.transform = `translateX(${offsetX}px) scale(${scale})`;
      inner.style.transform = `translateX(${-offsetX / scale}px) scale(${1 / scale})`;
    }

    /**
     * @param {HTMLElement} element
     * @param {number} opacity
     */
    #show(element, opacity) {
      element.style.opacity = String(opacity);
      element.style.visibility = opacity <= 0 ? 'hidden' : 'visible';
    }

    #render() {
      if (!this.geo) return;
      const time = /** @type {number} */ (this.time);
      const { cfg: config, el: parts, slide, zoom } = this.geo;

      const slideProgress = afterHold(tweenProgress(time, 0, 0.28));
      const zoomProgress = tweenProgress(time, 0.28, 0.3);
      if (time < 0.28) this.#mask(slide * slideProgress, 1 + 0.06 * slideProgress);
      else this.#mask(slide, 1.06 * zoom ** zoomProgress);
      parts.bg.style.transform = `scale(${1 + 0.06 * slideProgress})`;

      parts.dimOut.style.opacity = String(tweenProgress(time, 0.28, 0.1));
      this.#show(parts.tex, 1 - tweenProgress(time, 0.32, 0.1));
      parts.photo.style.opacity = String(
        time < 0.83 ? tweenProgress(time, 0.4, 0.14) : 1 - tweenProgress(time, 0.83, 0.01)
      );
      parts.vid.style.opacity = String(tweenProgress(time, 0.4, 0.14));
      this.#show(parts.wrap, 1 - tweenProgress(time, 0.55, 0.03));
      parts.shade.style.opacity = String(tweenProgress(time, 0.78, 0.04));

      const cardOffset = this.geo.cardFrom * (1 - tweenProgress(time, 0.62, 0.14));
      parts.card.style.transform = config.mobile
        ? `translateY(calc(-50% + ${cardOffset}px))`
        : `translateY(${cardOffset}px)`;
      parts.card.style.height = `${
        this.geo.cardH + (this.geo.cardEndH - this.geo.cardH) * tweenProgress(time, 0.83, 0.07)
      }px`;
      if (config.mobile) parts.card.style.top = `${config.cardTop - 100 * tweenProgress(time, 0.83, 0.07)}px`;

      const swapProgress = power1InOut(tweenProgress(time, 0.78, 0.06));
      parts.ca.style.transform = `translateY(${this.geo.caOut * swapProgress}px)`;
      parts.cb.style.transform = `translateY(${this.geo.cbIn * (1 - swapProgress)}px)`;
      parts.ca.style.visibility = swapProgress >= 1 ? 'hidden' : 'inherit';
      parts.cb.style.visibility = swapProgress <= 0 ? 'hidden' : 'inherit';
    }

    // Play the visible stage's videos while the section is within a screen of the viewport
    #videos() {
      const videos = Array.from(this.querySelectorAll('video'));
      if (!videos.length || this.reducedMotion) return;
      const viewportHeight = window.innerHeight;
      const rect = this.getBoundingClientRect();
      const near = rect.bottom > -viewportHeight && rect.top < 2 * viewportHeight;
      for (const video of videos) {
        if (near && !this.videosLoaded) {
          video.preload = 'auto';
          video.load();
        }
        if (near && video.offsetParent !== null) {
          if (video.paused) video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      }
      if (near) this.videosLoaded = true;
    }
  }

  customElements.define('pro2-ultra', Pro2Ultra);
}
